import type { ZudokuConfig } from "zudoku";

// --- Twin brand CSS: LT Superior fonts + landing card grid + polish ---------
const twinCss = /* css */ `
/* LT Superior — sans */
@font-face { font-family:'LT Superior'; src:url('/fonts/lt-superior-300.otf') format('opentype'); font-weight:300; font-style:normal; font-display:swap; }
@font-face { font-family:'LT Superior'; src:url('/fonts/lt-superior-400.otf') format('opentype'); font-weight:400; font-style:normal; font-display:swap; }
@font-face { font-family:'LT Superior'; src:url('/fonts/lt-superior-500.otf') format('opentype'); font-weight:500; font-style:normal; font-display:swap; }
@font-face { font-family:'LT Superior'; src:url('/fonts/lt-superior-600.otf') format('opentype'); font-weight:600; font-style:normal; font-display:swap; }
/* LT Superior — serif (display) */
@font-face { font-family:'LT Superior Serif'; src:url('/fonts/lt-superior-serif-400.otf') format('opentype'); font-weight:400; font-style:normal; font-display:swap; }
@font-face { font-family:'LT Superior Serif'; src:url('/fonts/lt-superior-serif-500.otf') format('opentype'); font-weight:500; font-style:normal; font-display:swap; }
/* LT Superior — mono */
@font-face { font-family:'LT Superior Mono'; src:url('/fonts/lt-superior-mono-500.otf') format('opentype'); font-weight:500; font-style:normal; font-display:swap; }
@font-face { font-family:'LT Superior Mono'; src:url('/fonts/lt-superior-mono-600.otf') format('opentype'); font-weight:600; font-style:normal; font-display:swap; }

:root {
  --font-sans: 'LT Superior', ui-sans-serif, system-ui, sans-serif;
  --font-serif: 'LT Superior Serif', Georgia, serif;
  --font-mono: 'LT Superior Mono', ui-monospace, 'SF Mono', monospace;
}
body { font-family: var(--font-sans); }
h1, h2, h3, .zudoku-heading { font-family: var(--font-serif); letter-spacing: -0.01em; }
code, pre, kbd { font-family: var(--font-mono); }

/* --- Landing: hero + card grid (Circle-Mint style) --- */
.tw-hero { margin: 0 0 2.5rem; }
.tw-hero h1 { font-size: clamp(2.1rem, 4vw, 3rem); margin: 0 0 .5rem; }
.tw-hero p.lead { font-size: 1.15rem; color: var(--muted-foreground, #6B6660); max-width: 46ch; margin: 0; }

.tw-cards {
  display: grid; gap: 1rem; margin: 1.5rem 0 2.5rem;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
}
.tw-card {
  display: block; padding: 1.25rem 1.35rem; border-radius: .75rem;
  border: 1px solid var(--border, #E7E2D6);
  background: var(--card, #FAF8F3);
  text-decoration: none !important; color: inherit;
  transition: border-color .15s ease, transform .15s ease, box-shadow .15s ease;
}
.tw-card:hover { border-color: #9C8746; transform: translateY(-2px); box-shadow: 0 6px 24px rgba(156,135,70,.12); }
.tw-card .tw-card-eyebrow { font-size: .72rem; text-transform: uppercase; letter-spacing: .08em; color: #9C8746; font-weight: 600; }
.tw-card h3 { margin: .4rem 0 .35rem; font-size: 1.15rem; font-family: var(--font-serif); }
.tw-card p { margin: 0; font-size: .92rem; color: var(--muted-foreground, #6B6660); line-height: 1.5; }

/* Gold accent for the token/contract tables */
.tw-cards + h2, .tw-hero + h2 { scroll-margin-top: 5rem; }
`;

const config: ZudokuConfig = {
  site: {
    title: "Twin",
    logo: {
      src: {
        light: "/twin-logomark-black.svg",
        dark: "/twin-logomark-gold.png",
      },
      alt: "Twin",
      width: "28px",
      href: "/",
    },
    footer: {
      copyright: `© ${new Date().getFullYear()} Twin Finance`,
    },
  },
  metadata: {
    title: "Twin Docs — Stablecoins de LATAM",
    description:
      "Documentación de Twin: stablecoins respaldadas 1:1 por monedas de LATAM. Redes activas, contratos, bridge cross-chain y prueba de reservas.",
    favicon: "/favicon.png",
  },
  theme: {
    customCss: twinCss,
    light: {
      background: "#FFFFFF",
      foreground: "#1A1712",
      card: "#FAF8F3",
      cardForeground: "#1A1712",
      popover: "#FFFFFF",
      popoverForeground: "#1A1712",
      primary: "#9C8746",
      primaryForeground: "#FFFFFF",
      secondary: "#F2EFE7",
      secondaryForeground: "#1A1712",
      muted: "#F2EFE7",
      mutedForeground: "#6B6660",
      accent: "#E2D79C",
      accentForeground: "#1A1712",
      border: "#E7E2D6",
      input: "#E7E2D6",
      ring: "#9C8746",
      radius: "0.5rem",
    },
    dark: {
      background: "#0D0D0D",
      foreground: "#F0EDE6",
      card: "#141414",
      cardForeground: "#F0EDE6",
      popover: "#141414",
      popoverForeground: "#F0EDE6",
      primary: "#E2D79C",
      primaryForeground: "#0D0D0D",
      secondary: "#1E1E1E",
      secondaryForeground: "#F0EDE6",
      muted: "#1A1A1A",
      mutedForeground: "#9A948B",
      accent: "#9C8746",
      accentForeground: "#F0EDE6",
      border: "#222222",
      input: "#222222",
      ring: "#E2D79C",
      radius: "0.5rem",
    },
  },
  docs: {
    files: "/**/*.{md,mdx}",
  },
  navigation: [
    {
      type: "category",
      label: "Overview",
      link: { type: "doc", file: "README", path: "/" },
      items: [
        "overview/architecture",
        { type: "doc", file: "overview/glossary", label: "Glosario (ES)" },
        {
          type: "category",
          label: "Glossary (EN)",
          link: "en/overview/glossary",
          items: ["en/overview/glossary"],
        },
      ],
    },
    {
      type: "category",
      label: "Stablecoins",
      link: "stablecoins/README",
      icon: "coins",
      items: [
        "stablecoins/supported-chains",
        "stablecoins/contracts",
        "stablecoins/bridge",
        "stablecoins/proof-of-reserves",
        "stablecoins/glossary",
        {
          type: "category",
          label: "English",
          link: "en/stablecoins/README",
          items: [
            "en/stablecoins/supported-chains",
            "en/stablecoins/contracts",
            "en/stablecoins/bridge",
            "en/stablecoins/proof-of-reserves",
            "en/stablecoins/glossary",
          ],
        },
      ],
    },
    {
      type: "category",
      label: "Consumer",
      link: "consumer/README",
      items: [
        "consumer/connect-wallet",
        "consumer/mint-redeem",
        "consumer/bridge",
        "consumer/swap",
        "consumer/migration",
      ],
    },
    // "Developer" and "Operations" are INTERNAL: every page under developer/ and
    // operations/ carries `draft: true`, so they render in local `make dev` (and
    // the sidebar below) but are EXCLUDED from the production build / public site.
    // Keep new internal pages marked `draft: true`.
    {
      type: "category",
      label: "Developer (interno)",
      link: "developer/README",
      items: [
        {
          type: "category",
          label: "Arquitectura",
          link: "developer/architecture/README",
          items: [
            "developer/architecture/backend",
            "developer/architecture/dashboard",
            "developer/architecture/platform",
            "developer/architecture/bridge-contracts",
          ],
        },
        {
          type: "category",
          label: "Flujos Técnicos",
          link: "developer/flows/README",
          items: [
            "developer/flows/minting",
            "developer/flows/bridge",
            "developer/flows/uniswap-liquidity",
            "developer/flows/oracle",
            "developer/flows/auto-rebalance",
            "developer/flows/rewards",
            "developer/flows/auth",
            "developer/flows/circulation",
            "developer/flows/disperse",
            "developer/flows/market-data",
            "developer/flows/supply-api",
            "developer/flows/contract-data",
            "developer/flows/cron-details",
          ],
        },
        {
          type: "category",
          label: "Base de Datos",
          link: "developer/database/README",
          items: [
            "developer/database/er-diagram",
            "developer/database/migrations",
            "developer/database/patterns",
          ],
        },
        "developer/local-setup",
        "developer/conventions",
      ],
    },
    {
      type: "category",
      label: "Operations (interno)",
      link: "operations/README",
      items: [
        {
          type: "category",
          label: "Dashboard Guide",
          link: "operations/dashboard-guide/README",
          items: [
            "operations/dashboard-guide/cron-jobs",
            "operations/dashboard-guide/minting",
            "operations/dashboard-guide/circulation",
            "operations/dashboard-guide/uniswap-pools",
            "operations/dashboard-guide/bridge-admin",
            "operations/dashboard-guide/rewards",
            "operations/dashboard-guide/balance-tracking",
            "operations/dashboard-guide/oracle-config",
            "operations/dashboard-guide/tokens",
          ],
        },
        {
          type: "category",
          label: "Deploy",
          link: "operations/deploy/README",
          items: [
            "operations/deploy/backend",
            "operations/deploy/frontend",
            "operations/deploy/migrations-checklist",
          ],
        },
        "operations/monitoring",
        "operations/contracts-addresses",
        "operations/troubleshooting",
      ],
    },
  ],
  redirects: [{ from: "/overview", to: "/" }],
};

export default config;
